// LearnCore Frontend - 任务管理页面

import React, { useState, useMemo } from 'react';
import TaskList from '../components/TaskList';
import CreateTaskForm from '../components/CreateTaskForm';
import DKPBalanceComponent from '../components/DKPBalance';
import { TaskUpdatesProvider, useTaskUpdates, useStatisticsRealtimeUpdates } from '../contexts/TaskUpdatesContext';
import type { UserTask } from '../types/tasks-rewards';

// 内部组件，使用Context
const TasksPageContent: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'in_progress' | 'completed'>('all');
  const [filters, setFilters] = useState<any>({});
  const [sortBy, setSortBy] = useState<'created_at' | 'due_date' | 'priority' | 'dkp_reward'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // 使用Context获取实时数据
  const { tasks, refreshAll, updateTaskOptimistic, loading } = useTaskUpdates();
  const { statistics } = useStatisticsRealtimeUpdates();

  const handleTaskCreated = () => {
    setShowCreateForm(false);
    refreshAll(); // 刷新所有数据
  };

  const handleTaskUpdated = (task: UserTask) => {
    // 乐观更新任务
    updateTaskOptimistic(task.id, task);
    // 后台刷新数据
    refreshAll();
  };

  const handleTaskDeleted = () => {
    refreshAll(); // 刷新所有数据
  };

  const getFilters = () => {
    const baseFilters = { ...filters };

    switch (activeTab) {
      case 'pending':
        return { ...baseFilters, status: ['pending'] };
      case 'in_progress':
        return { ...baseFilters, status: ['in_progress'] };
      case 'completed':
        return { ...baseFilters, status: ['completed'] };
      default:
        return baseFilters;
    }
  };

  const handleFilterChange = (filterType: string, value: any) => {
    setFilters((prev: any) => ({
      ...prev,
      [filterType]: value ? value : undefined
    }));
  };

  const handleSortChange = (field: 'created_at' | 'due_date' | 'priority' | 'dkp_reward') => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // 计算任务统计
  const taskStats = useMemo(() => {
    const allCount = tasks.length;
    const pendingCount = tasks.filter(t => t.status === 'pending').length;
    const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
    const completedCount = tasks.filter(t => t.status === 'completed').length;
    
    return {
      all: allCount,
      pending: pendingCount,
      in_progress: inProgressCount,
      completed: completedCount
    };
  }, [tasks]);

  return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* 页面标题 */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">任务管理</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">管理和跟踪你的学习任务，赚取DKP积分</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 左侧边栏 */}
          <div className="lg:col-span-1 space-y-6">
            {/* DKP余额 */}
            <DKPBalanceComponent compact={true} />

            {/* 快速统计 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">快速统计</h3>
                <button
                  onClick={refreshAll}
                  disabled={loading}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="刷新数据"
                >
                  {loading ? '刷新中...' : '刷新'}
                </button>
              </div>
              {statistics ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">今日任务</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {statistics.today?.tasks_total || 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">本周完成</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {statistics.week?.tasks_completed || 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">连续天数</span>
                    <span className="font-medium text-orange-600 dark:text-orange-400">
                      {statistics.streak?.consecutive_days || 0}天
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">本月DKP</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      +{statistics.month?.dkp_earned || 0}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 text-sm py-2">
                  加载统计数据中...
                </div>
              )}
            </div>

            {/* 创建任务按钮 */}
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full bg-blue-600 text-white rounded-lg py-3 px-4 font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              创建新任务
            </button>
          </div>

          {/* 主内容区域 */}
          <div className="lg:col-span-3">
            {/* 标签页 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="flex -mb-px">
                  {[
                    { key: 'all', label: '全部任务', count: taskStats.all },
                    { key: 'pending', label: '待开始', count: taskStats.pending },
                    { key: 'in_progress', label: '进行中', count: taskStats.in_progress },
                    { key: 'completed', label: '已完成', count: taskStats.completed }
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key as any)}
                      className={`py-3 px-6 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab.key
                          ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                      }`}
                    >
                      {tab.label}
                      <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs dark:bg-gray-700 dark:text-gray-300">
                        {tab.count}
                      </span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* 筛选和排序 */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <select
                      value={filters.category || ''}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">所有分类</option>
                      <option value="study_time">学习时间</option>
                      <option value="cards_completed">完成卡片</option>
                      <option value="accuracy_rate">正确率</option>
                      <option value="consecutive_days">连续天数</option>
                      <option value="total_mastered">掌握词汇</option>
                      <option value="content_created">创建内容</option>
                      <option value="custom">自定义</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <select
                      value={filters.type || ''}
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">所有类型</option>
                      <option value="daily">日常任务</option>
                      <option value="habit">习惯任务</option>
                      <option value="achievement">成就任务</option>
                      <option value="custom">自定义任务</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <select
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value as 'created_at' | 'due_date' | 'priority' | 'dkp_reward')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="created_at">创建时间</option>
                      <option value="due_date">截止日期</option>
                      <option value="priority">优先级</option>
                      <option value="dkp_reward">DKP奖励</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* 任务列表 */}
            <TaskList
              tasks={tasks}
              filters={getFilters()}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onTaskUpdate={handleTaskUpdated}
              onTaskDelete={handleTaskDeleted}
              className="space-y-4"
            />
          </div>
        </div>

        {/* 创建任务表单模态框 */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CreateTaskForm
                onSuccess={handleTaskCreated}
                onCancel={() => setShowCreateForm(false)}
                className="m-4"
              />
            </div>
          </div>
        )}
      </div>
  );
};

// 主组件，包含Provider
const TasksPage: React.FC = () => {
  return (
    <TaskUpdatesProvider autoRefresh={true} refreshInterval={30}>
      <TasksPageContent />
    </TaskUpdatesProvider>
  );
};

export default TasksPage;